import ApiaField from '../ApiaField';
import ApiaForm from '../ApiaForm';
import Field from '../constants/Field';
import { IApiaSelect, TApiaSelectPossibleValue } from '../../types';
import { IApiaField } from '../types';
import propsStore from '../../store/propsStore/propsStore';

export type TSelectOptionsAsObj = {
  [key: string]: string;
};

export type TSelectOptionsAsArray = (string | number)[][];

export interface ISelectField extends IApiaField {
  getOptions(
    asObject: boolean,
  ): TSelectOptionsAsObj | TSelectOptionsAsArray | null;
  addOption(
    optionValue: string,
    showValue: string,
    allowRepeatedValue: boolean,
  ): SelectField | null;
  setOptions(
    optionsArray: TApiaSelectPossibleValue[] | TApiaSelectPossibleValue,
  ): SelectField | null;
  removeOption(optionValue: string | number): SelectField | null;
  getSelectedOption(
    asObject: boolean,
  ):
    | TSelectOptionsAsObj
    | string[]
    | string[][]
    | void
    | undefined
    | (string | number)[][]
    | null;
  getSelectedText(): string | null;
  clearOptions(): ISelectField | null;
}

export default class SelectField extends ApiaField implements ISelectField {
  constructor(
    field: HTMLSelectElement | HTMLDivElement,
    form: ApiaForm,
    boolOptions: string[] = [],
  ) {
    super(
      field,
      form,
      boolOptions || [
        Field.PROPERTY_INPUT_AS_TEXT,
        Field.PROPERTY_REQUIRED,
        Field.PROPERTY_DISABLED,
        Field.PROPERTY_READONLY,
        Field.PROPERTY_VISIBILITY_HIDDEN,
        Field.PROPERTY_SHOW_TOOLTIP_AS_HELP,
      ],
    );
  }

  // get all select options either as array[] or object
  getOptions(
    asObject: boolean,
  ): TSelectOptionsAsObj | TSelectOptionsAsArray | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const props = propsStore.getFieldProps(this.fldId);
      const options = (props.field as IApiaSelect)
        .possibleValue as TApiaSelectPossibleValue[];
      if (asObject) {
        const optionsResult = {} as TSelectOptionsAsObj;
        for (let i = 0; i < options.length; i += 1) {
          if (options[i]) {
            optionsResult[options[i].value] = options[i].label;
          }
        }
        return optionsResult;
      }
      const optionsResult = [];
      for (let i = 0; i < options.length; i += 1) {
        const currentOption = [];
        currentOption.push(options[i].value);
        currentOption.push(options[i].label);
        optionsResult.push(currentOption);
      }
      return optionsResult;
    }
    return null;
  }

  // adds a option to the select field
  addOption(
    optionValue: string | number,
    showValue: string,
    allowRepeatedValue: boolean,
  ): SelectField | null {
    const dataArray = [];
    let isValueRepeated = false;
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const props = propsStore.getFieldProps(this.fldId);
      const possibleValue = (props.field as IApiaSelect)
        .possibleValue as TApiaSelectPossibleValue[];
      for (let i = 0; i < possibleValue.length; i += 1) {
        if (optionValue === possibleValue[i].value) {
          isValueRepeated = true;
        } else {
          isValueRepeated = false;
        }
        dataArray.push({
          value: possibleValue[i].value,
          label: possibleValue[i].label,
        });
      }
      if (allowRepeatedValue || (!isValueRepeated && !allowRepeatedValue)) {
        dataArray.push({
          value: optionValue,
          label: showValue,
        });
      }
      propsStore.updateField(this.fldId, { possibleValue: dataArray });
      return this;
    }
    return null;
  }

  // overwrite all options in select field
  setOptions(
    optionsArray: TApiaSelectPossibleValue[] | TApiaSelectPossibleValue,
  ): SelectField | null {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      propsStore.updateField(this.fldId as string, {
        possibleValue: optionsArray,
      });
      return this;
    }
    return null;
  }

  removeOption(optionValue: string | number): SelectField | null {
    const optionsArray = [];
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const props = propsStore.getFieldProps(this.fldId);
      const possibleValue = (props.field as IApiaSelect)
        .possibleValue as TApiaSelectPossibleValue[];
      for (let i = 0; i < possibleValue.length; i += 1) {
        if (optionValue.toString() !== possibleValue[i].value.toString()) {
          optionsArray.push({
            value: possibleValue[i].value,
            label: possibleValue[i].label,
          });
        }
      }
      propsStore.updateField(this.fldId, {
        possibleValue: optionsArray,
      });
      return this;
    }
    return null;
  }

  // get selected option either as array or object key/value
  getSelectedOption(
    asObject: boolean,
  ):
    | TSelectOptionsAsObj
    | string[]
    | string[][]
    | void
    | undefined
    | (string | number)[][]
    | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const props = propsStore.getFieldProps(this.fldId);
      const possibleValues = (props.field as IApiaSelect)
        .possibleValue as TApiaSelectPossibleValue[];
      const { value } = props;
      if (possibleValues && value !== undefined && typeof value === 'string') {
        const selected = possibleValues.find((possible) => {
          return possible.value === value.toString();
        });
        if (asObject) {
          const optionsResult = {} as TSelectOptionsAsObj;
          if (selected) {
            optionsResult[value] = selected.label || '';
          }
          return optionsResult;
        }
        if (selected) {
          return [value || '', selected.label || ''];
        }
      }
    }
    return null;
  }

  getSelectedText(): string | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const props = propsStore.getFieldProps(this.fldId);
      const possibleValue = (props.field as IApiaSelect)
        .possibleValue as TApiaSelectPossibleValue[];
      const { value } = props;
      const selected = possibleValue?.find((possible) => {
        return possible.value === (value as string);
      });
      return selected?.label || '';
    }
    return null;
  }

  // // remove all select options
  clearOptions(): ISelectField | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      propsStore.updateField(this.fldId, {
        possibleValue: [],
      });
      return this;
    }
    return null;
  }
}
